import React, { useState } from 'react';
import Button from './Button';
import { DEFAULT_MODEL_ID, MODELS } from '../constants';

interface RemarkModelsSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedModelIds: string[]) => void;
  essayCount: number;
}

const RemarkModelsSelectionModal: React.FC<RemarkModelsSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  essayCount,
}) => {
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([DEFAULT_MODEL_ID]);
  const [error, setError] = useState<string>('');

  const handleModelToggle = (modelId: string) => {
    const newSelection = selectedModelIds.includes(modelId)
      ? selectedModelIds.filter(id => id !== modelId)
      : [...selectedModelIds, modelId];
    
    setSelectedModelIds(newSelection);
    if (newSelection.length > 0) {
      setError('');
    }
  };

  const handleConfirm = () => {
    if (selectedModelIds.length === 0) {
      setError('Please select at least one AI model for remarking.');
      return;
    }
    onConfirm(selectedModelIds);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Select AI Models for Remarking
          </h2>
          <p className="text-gray-600">
            Choose which AI models you want to use for remarking {essayCount} selected essay{essayCount !== 1 ? 's' : ''}. 
            You can select multiple models for comparison.
          </p>
        </div>

        <div className="p-6 overflow-y-auto max-h-96">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.values(MODELS).map((model) => (
              <div key={model.id} className="relative">
                <label className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                  selectedModelIds.includes(model.id)
                    ? 'border-primary-500 bg-primary-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-sm'
                }`}>
                  <input
                    type="checkbox"
                    checked={selectedModelIds.includes(model.id)}
                    onChange={() => handleModelToggle(model.id)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1 flex-shrink-0"
                  />
                  <div className="ml-3 min-w-0">
                    <div className="font-medium text-gray-900">{model.name}</div>
                    <div className="text-sm text-gray-600 mt-1">{model.description}</div>
                  </div>
                </label>
              </div>
            ))}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {selectedModelIds.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Selected models:</strong> {selectedModelIds.map(id => MODELS[id].name).join(', ')}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Each essay will be remarked by all {selectedModelIds.length} selected model{selectedModelIds.length !== 1 ? 's' : ''}.
              </p>
            </div>
          )}

          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.924-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Important Note</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Remarking will create new AI assessments for each selected model. Previous AI results may be overwritten. 
                  Manual scores will remain unchanged.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            onClick={onClose}
            variant="secondary"
            className="order-2 sm:order-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="primary"
            disabled={selectedModelIds.length === 0}
            className="order-1 sm:order-2"
          >
            Start Remarking ({selectedModelIds.length} model{selectedModelIds.length !== 1 ? 's' : ''})
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RemarkModelsSelectionModal;
